import type { ExecutionSummary, Me, Member, Role, TaskRow, Team, WebhookSummary } from '@/api'
import { memberLabel } from '@/lib/memberLabel'

/**
 * What the command palette can jump to, and how a typed query picks it. The component owns the
 * shortcuts and the fetching; everything here is pure so the matching is testable on its own.
 *
 * Order matters twice: it is the order results are listed when two records score the same, and it
 * is the order the CEO reads them in (the enterprise, then its circles, then the work).
 */
export const PALETTE_KINDS = ['enterprise', 'team', 'member', 'role', 'task', 'execution', 'webhook'] as const

export type PaletteKind = (typeof PALETTE_KINDS)[number]

/** The badge each result carries, so a row says what kind of record it is before you jump. */
export const PALETTE_LABELS: Record<PaletteKind, string> = {
    enterprise: 'Enterprise',
    team: 'Team',
    member: 'Member',
    role: 'Role',
    task: 'Task',
    execution: 'Execution',
    webhook: 'Webhook',
}

export type PaletteResult = {
    /** Unique in one list: two record types can share an id. */
    key: string
    kind: PaletteKind
    label: string
    title: string
    subtitle: string | null
    /** The record's real detail route. Webhooks live at the singular `/webhook/:id`. */
    to: string
    /** Everything a query may match on, lower-cased: name, slug, id, and the names around it. */
    terms: string[]
}

export type PaletteSources = {
    enterprise: Me['enterprise'] | null
    teams: Team[]
    members: Member[]
    roles: Role[]
    tasks: TaskRow[]
    executions: ExecutionSummary[]
    webhooks: WebhookSummary[]
}

export function emptyPaletteSources(): PaletteSources {
    return { enterprise: null, teams: [], members: [], roles: [], tasks: [], executions: [], webhooks: [] }
}

function terms(values: Array<string | null | undefined>): string[] {
    const kept: string[] = []
    for (const value of values) {
        if (typeof value === 'string' && value.trim() !== '') {
            kept.push(value.trim().toLowerCase())
        }
    }
    return kept
}

function join(parts: Array<string | null | undefined>): string | null {
    const kept = parts.filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    return kept.length > 0 ? kept.join(' · ') : null
}

function result(
    kind: PaletteKind,
    id: string,
    to: string,
    title: string,
    subtitle: string | null,
    extraTerms: Array<string | null | undefined>,
): PaletteResult {
    return {
        key: `${kind}:${id}`,
        kind,
        label: PALETTE_LABELS[kind],
        title,
        subtitle,
        to,
        terms: terms([title, ...extraTerms]),
    }
}

function teamResults(teams: Team[]): PaletteResult[] {
    return teams.map(team =>
        result('team', team.id, `/teams/${team.id}`, team.name, join([team.slug, team.leadName]), [
            team.slug,
            team.id,
            team.leadName,
        ]),
    )
}

function memberResults(members: Member[]): PaletteResult[] {
    return members.map(member =>
        result('member', member.id, `/members/${member.id}`, memberLabel(member), join([member.email]), [
            member.email,
            member.id,
            ...member.roles.map(role => role.name),
        ]),
    )
}

function roleResults(roles: Role[]): PaletteResult[] {
    return roles.map(role =>
        result('role', role.id, `/roles/${role.id}`, role.name, join([role.slug]), [role.slug, role.id]),
    )
}

/** A task is found by the teams that inherit it: each team where a member holds its role. */
function taskResults(tasks: TaskRow[]): PaletteResult[] {
    return tasks.map(task => {
        const teamNames = (task.teams ?? []).map(team => team.name)
        return result(
            'task',
            task.id,
            `/tasks/${task.id}`,
            task.name ?? task.slug,
            join([task.assigneeRoleName, ...teamNames, task.slug]),
            [task.slug, task.id, task.assigneeRoleName, task.assigneeRole, ...teamNames, task.status],
        )
    })
}

function executionResults(executions: ExecutionSummary[]): PaletteResult[] {
    return executions.map(execution =>
        result(
            'execution',
            execution.id,
            `/executions/${execution.id}`,
            execution.name,
            join([execution.status, ...execution.members.map(member => member.name)]),
            [
                execution.startingTask.name,
                execution.startingTask.slug,
                ...execution.resources.map(resource => resource.name),
                execution.id,
                execution.rootRunId,
                execution.status,
                ...execution.teams.map(team => team.name),
                ...execution.members.map(member => member.name),
                ...execution.roles.map(role => role.name),
            ],
        ),
    )
}

function webhookResults(webhooks: WebhookSummary[]): PaletteResult[] {
    return webhooks.map(webhook =>
        // Singular on purpose: the detail route is `/webhook/:id`, unlike every other list page.
        result(
            'webhook',
            webhook.id,
            `/webhook/${webhook.id}`,
            webhook.target.name,
            join([webhook.target.kind, webhook.status]),
            [webhook.id, webhook.target.id, webhook.target.kind, webhook.status],
        ),
    )
}

/** Everything the palette can jump to, flattened into one searchable list. */
export function paletteEntries(sources: PaletteSources): PaletteResult[] {
    const enterprise = sources.enterprise
    const enterpriseResults = enterprise
        ? [
              result('enterprise', enterprise.id, '/enterprise', enterprise.name, enterprise.slug, [
                  enterprise.slug,
                  enterprise.id,
              ]),
          ]
        : []
    return [
        ...enterpriseResults,
        ...teamResults(sources.teams),
        ...memberResults(sources.members),
        ...roleResults(sources.roles),
        ...taskResults(sources.tasks),
        ...executionResults(sources.executions),
        ...webhookResults(sources.webhooks),
    ]
}

/** How well one field matches: 0 the whole field, 1 its start, 2 anywhere inside it. */
function termHit(term: string, needle: string): number | null {
    if (term === needle) {
        return 0
    }
    const index = term.indexOf(needle)
    if (index < 0) {
        return null
    }
    return index === 0 ? 1 : 2
}

/** Lower is better: whole-field hit, then prefix, then anywhere, then every word somewhere. */
function score(entry: PaletteResult, needle: string, words: string[]): number | null {
    let best: number | null = null
    for (const term of entry.terms) {
        const hit = termHit(term, needle)
        if (hit !== null && (best === null || hit < best)) {
            best = hit
        }
    }
    if (best !== null) {
        return best
    }
    const haystack = entry.terms.join(' ')
    return words.every(word => haystack.includes(word)) ? 3 : null
}

/** The rows the palette shows for a query. An empty query matches nothing, on purpose. */
export function searchPalette(entries: PaletteResult[], query: string, limit = 12): PaletteResult[] {
    const needle = query.trim().toLowerCase()
    if (needle === '') {
        return []
    }
    const words = needle.split(/\s+/)
    const scored: Array<{ entry: PaletteResult; rank: number }> = []
    for (const entry of entries) {
        const rank = score(entry, needle, words)
        if (rank !== null) {
            scored.push({ entry, rank })
        }
    }
    scored.sort(
        (left, right) =>
            left.rank - right.rank ||
            PALETTE_KINDS.indexOf(left.entry.kind) - PALETTE_KINDS.indexOf(right.entry.kind) ||
            left.entry.title.localeCompare(right.entry.title),
    )
    return scored.slice(0, limit).map(item => item.entry)
}
